import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getTierLimits } from '../lib/capabilities';
import { useUserTier } from './useUserTier';

type EntityType = 'task' | 'note' | 'goal' | 'event' | 'contact' | 'habit';

export interface CanCreateResult {
  allowed: boolean;
  reason: 'node_limit' | 'sub_limit' | null;
  message: string | null;
  tierLimit: number | null;
}

const ALLOWED: CanCreateResult = { allowed: true, reason: null, message: null, tierLimit: null };

const TABLE_NAMES: readonly EntityType[] = [
  'task',
  'note',
  'goal',
  'event',
  'contact',
  'habit',
] as const;

const ENTITY_TABLE_MAP: Record<EntityType, string> = {
  task: 'tasks',
  note: 'notes',
  goal: 'goals',
  event: 'events',
  contact: 'contacts',
  habit: 'habits',
};

export function useCanCreate() {
  const tier = useUserTier();

  const checkCanCreate = useCallback(
    async (entityType: EntityType): Promise<CanCreateResult> => {
      const limits = getTierLimits(tier);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return ALLOWED; // unauthenticated → let other guards handle

      // ── 1. Sub-limit checks ──────────────────────────────────────────────
      if (entityType === 'goal' && limits.maxObjectives !== -1) {
        const { count } = await supabase
          .from('goals')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if ((count ?? 0) >= limits.maxObjectives) {
          return {
            allowed: false,
            reason: 'sub_limit',
            message: `Limite de ${limits.maxObjectives} objectifs atteinte sur le plan gratuit. Passez à Pro pour en créer davantage.`,
            tierLimit: limits.maxObjectives,
          };
        }
      }

      if (entityType === 'contact' && limits.maxContacts !== -1) {
        const { count } = await supabase
          .from('contacts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if ((count ?? 0) >= limits.maxContacts) {
          return {
            allowed: false,
            reason: 'sub_limit',
            message: `Limite de ${limits.maxContacts} contacts atteinte sur le plan gratuit. Passez à Pro pour en créer davantage.`,
            tierLimit: limits.maxContacts,
          };
        }
      }

      if (entityType === 'habit' && limits.maxHabits !== -1) {
        const { count } = await supabase
          .from('habits')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if ((count ?? 0) >= limits.maxHabits) {
          return {
            allowed: false,
            reason: 'sub_limit',
            message: `Limite de ${limits.maxHabits} habitudes atteinte sur le plan gratuit. Passez à Pro pour en créer davantage.`,
            tierLimit: limits.maxHabits,
          };
        }
      }

      // ── 2. Global node limit ─────────────────────────────────────────────
      if (limits.maxNodes !== -1) {
        const counts = await Promise.all(
          TABLE_NAMES.map((t) =>
            supabase
              .from(ENTITY_TABLE_MAP[t])
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id)
              .then(({ count: c }) => c ?? 0)
          )
        );

        const totalCount = counts.reduce((sum, c) => sum + c, 0);

        if (totalCount >= limits.maxNodes) {
          return {
            allowed: false,
            reason: 'node_limit',
            message: `Vous avez atteint la limite de ${limits.maxNodes} éléments sur votre plan gratuit. Passez à Pro pour créer jusqu'à 500 éléments.`,
            tierLimit: limits.maxNodes,
          };
        }
      }

      return ALLOWED;
    },
    [tier]
  );

  return { checkCanCreate };
}
